// src/portal-app/shell/kit/HeroStat.js
//
// The big-number treatment. Every hero card in the app (Home, list screens,
// verdict cards) renders one of these so the primary figure has exactly one
// size, weight and delta-pill vocabulary across the product.
import React from 'react';
import { ensureKitCss } from './kitCss';
import { useLanguage } from '../../../context/LanguageContext';
import { IconArrowUp, IconArrowDown } from './icons';
import { Sparkline } from './Sparkline';

const DELTA_CLASS = { up: 'pk-delta-up', down: 'pk-delta-down', flat: 'pk-delta-flat' };

// 3B (spec §12a, verified defect): the IconArrowUp/IconArrowDown glyphs
// inside .pk-delta are aria-hidden, so direction was carried by colour and
// a hidden icon alone (spec §9 forbids colour as the sole carrier of
// meaning). An earlier fix-wave added `deltaLabel` as an opt-in prop, but
// an opt-in a caller can forget reintroduces the exact defect for every
// consumer that doesn't pass it — and 9 more Plan 4 screens are about to
// consume this kit. The label is now computed automatically from
// `delta.direction`/`delta.value`; `deltaLabel` still wins when a screen
// needs bespoke wording (e.g. HomeTab's existing composition).
export function HeroStat({
    label, value, delta, deltaLabel, caption, points, sparkAriaLabel,
}) {
    ensureKitCss();
    const { t } = useLanguage();
    const dir = delta?.direction || 'flat';
    const autoDeltaLabel = delta && dir === 'up'
        ? t('delta_up', { value: delta.value }, 'Up {value}')
        : delta && dir === 'down'
            ? t('delta_down', { value: delta.value }, 'Down {value}')
            : undefined;
    const deltaAriaLabel = deltaLabel || autoDeltaLabel;

    return (
        <div className="pk-hero">
            {label && <div className="pk-label">{label}</div>}
            <div className="pk-hero-value pk-num">{value}</div>
            {(delta || caption) && (
                <div className="pk-hero-meta">
                    {delta && (
                        <span
                            className={`pk-delta ${DELTA_CLASS[dir] || DELTA_CLASS.flat}`}
                            aria-label={deltaAriaLabel || undefined}
                        >
                            {dir === 'up' && <IconArrowUp />}
                            {dir === 'down' && <IconArrowDown />}
                            <span className="pk-num">{delta.value}</span>
                        </span>
                    )}
                    {caption && <span className="pk-hero-caption">{caption}</span>}
                </div>
            )}
            {Array.isArray(points) && points.length >= 2 && (
                <div className="pk-hero-spark">
                    <Sparkline points={points} ariaLabel={sparkAriaLabel} />
                </div>
            )}
        </div>
    );
}

export default HeroStat;
